import React from 'react'
import ProfessionalChartLoader from '../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { EChartsPresenter } from '../../../common/components/molecule/EChartsView'
import { EChartsSeries } from '../../../common/components/molecule/EChartsView/EChartsView'
import { MOMI_SERIES_COLORS } from '../../minigrid/components/LiveEnergyDashboard/momiTelemetryChart.utils'
import { EvolutionPoint } from '../types/joulyDailyTypes'
import { useMinigridNamesByUuid } from '../service/useMinigridNamesByUuid'

type Props = {
  data: EvolutionPoint[]
  loading?: boolean
  error?: Error | null
}

const TITLE = 'Evolution des appareils actifs'

export default function EvolutionLinePresenter({ data, loading, error }: Props) {
  const { resolveName } = useMinigridNamesByUuid()

  if (loading) {
    return <ProfessionalChartLoader metricName={TITLE} />
  }

  if (error) {
    return <ChartErrorView metricName={TITLE} error={error} />
  }

  if (!data || data.length === 0) {
    return (
      <EChartsPresenter
        data={[]}
        title={TITLE}
        type="line"
        height={400}
      />
    )
  }

  // Group points by uuid (one minigrid = one MoMi gateway), sort each series by time
  const byUuid = new Map<string, EvolutionPoint[]>()
  for (const pt of data) {
    const existing = byUuid.get(pt.uuid) ?? []
    existing.push(pt)
    byUuid.set(pt.uuid, existing)
  }

  const uuids = Array.from(byUuid.keys()).sort((a, b) =>
    resolveName(a).localeCompare(resolveName(b), 'fr')
  )

  // Collect all unique time labels across all series (sorted)
  const allTimes = Array.from(
    new Set(data.map((pt) => pt.time))
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  const xLabels = allTimes.map((t) => {
    const d = new Date(t)
    return d.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
  })

  const multiSeries: EChartsSeries[] = uuids.map((uuid, idx) => {
    const points = byUuid.get(uuid) ?? []
    const pointsByTime = new Map<string, number>()
    for (const pt of points) {
      pointsByTime.set(pt.time, pt.count)
    }
    return {
      name: resolveName(uuid),
      color: MOMI_SERIES_COLORS[idx % MOMI_SERIES_COLORS.length],
      data: allTimes.map((t, i) => ({
        name: xLabels[i],
        value: pointsByTime.get(t) ?? null,
      })),
    }
  })

  return (
    <EChartsPresenter
      data={multiSeries}
      title={TITLE}
      type="line"
      height={400}
      xAxisLabel="Date"
      yAxisLabel="Appareils actifs"
      showLegend={true}
      showTooltip={true}
    />
  )
}
